import { useCallback, useEffect, useRef, useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { DirVendorType } from '@econtractor/types';
import { useSettings } from 'context/SettingsContext';
import * as apiSettings from 'api/apiSettings';
import * as apiProduct from 'api/apiProducts';
import { useToast } from 'context/ToastContext';
import ESpinner from 'components/others/Spinner';
import AddVendorFormik from './AddVendorFormik';
import VendorsList from './VendorsList';

interface OffVendorsProps {
    show: boolean;
    setShow: (show: boolean) => void;
}

interface VendorItem {
    ID: number;
    vendorName: string;
    vendorEmail: string;
}

const extractVendors = (settings: { DIRS?: { Vendor?: DirVendorType[] } } | null): VendorItem[] =>
    (settings?.DIRS?.Vendor || [])
        .filter((vendor) => !vendor.hidden && vendor.ID)
        .map((vendor) => ({
            ID: vendor.ID as number,
            vendorName: String(vendor.vendorName || ''),
            vendorEmail: String(vendor.vendorEmail || ''),
        }));

const OffVendors = ({ show, setShow }: OffVendorsProps) => {
    const { t } = useTranslation(['Products', 'common']);
    const { showToast } = useToast();
    const { dispatchLocalSettings } = useSettings();
    const showToastRef = useRef(showToast);
    const tRef = useRef(t);

    showToastRef.current = showToast;
    tRef.current = t;

    const [vendors, setVendors] = useState<VendorItem[]>([]);
    const [usage, setUsage] = useState<Record<number, number>>({});
    const [loading, setLoading] = useState(false);

    const applyData = useCallback(
        (resSetting: NonNullable<Awaited<ReturnType<typeof apiSettings.getSettings>>>, resUsage: Record<number, number>) => {
            dispatchLocalSettings({ type: 'SET', settings: resSetting });
            setVendors(extractVendors(resSetting));
            setUsage(resUsage);
        },
        [dispatchLocalSettings],
    );

    const fetchData = useCallback(async () => {
        const [resSetting, resUsage] = await Promise.all([apiSettings.getSettings(), apiProduct.getVendorUsage()]);
        if (!resSetting || resUsage === null) return null;
        return { resSetting, resUsage };
    }, []);

    const syncData = useCallback(async (): Promise<void> => {
        const data = await fetchData();
        if (!data) {
            showToastRef.current(tRef.current('common:messError'), 'danger');
            return;
        }
        applyData(data.resSetting, data.resUsage);
    }, [applyData, fetchData]);

    useEffect(() => {
        if (!show) return;

        let cancelled = false;

        (async () => {
            setLoading(true);
            const data = await fetchData();
            if (cancelled) return;

            setLoading(false);
            if (!data) {
                showToastRef.current(tRef.current('common:messError'), 'danger');
                return;
            }
            applyData(data.resSetting, data.resUsage);
        })();

        return () => {
            cancelled = true;
        };
    }, [show, applyData, fetchData]);

    const handleClose = () => setShow(false);

    return (
        <Offcanvas show={show} onHide={handleClose} placement="end">
            <Offcanvas.Header className="border-bottom" closeButton>
                <Offcanvas.Title>{t('vendorsEditor.title')}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                <div className="off-coming-form d-flex flex-column h-100">
                    <AddVendorFormik onCreated={syncData} />

                    <div className="off-coming-content">
                        {loading ? <ESpinner /> : <VendorsList vendors={vendors} usage={usage} onChanged={syncData} />}
                    </div>
                </div>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffVendors;
